import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import { FaHeadset } from "react-icons/fa";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import PrivateRoute from "./routing/PrivateRoute";
import { Navbar, Footer, Sidebar } from "./components";
import {
  Home,
  GetStarted,
  Profile,
  Classes,
  ArchivedStudents,
  GraduatedStudents,
  AddClass,
  UpdateClass,
  Students,
  Subjects,
  LessonNotes,
  AddAddons,
  Addons,
  UpdateAddon,
  Transportation,
  AddTransportation,
  UpdateTransport,
  Discounts,
  AllTransactions,
  TransactionDetails,
  TransactionPerClass,
  Flexpay,
  FlexpayDetails,
  FlexpayHistory,
  WithdrawBalance,
  WithdrawCredit,
  Compliance,
  BusinessCompliance,
  PaymentCompliance,
  Cashflow,
  CashflowPending,
  InvoiceDetails,
  ClassPayments,
  SignIn,
  SignUp,
  Welcome,
  Onboarding,
  Documents,
  Completed,
  Reset,
  Recover,
  Support,
  DirectorsIdentity,
  PaymentDetails,
  UpdateStudent,
  AddonPayments,
  PaymentPerAddon,
  Transfer,
  ExpenseRecord,
  NotFound,
  ClassAddons,
  ExpenseDetails,
  TeacherManagement,
  TeacherAttendance,
  DeviceApprovalManagement,
  ViewTeacher,
  AcceptInvite,
  StudentProfile,
  AttendanceManagement,
  ClockInOutHistory,
  ResultManagement,
  Summary,
  Dashboard,
  TeacherClassManagement,
  TeacherStudents,
  TeacherSubjects,
  TeacherLessonNotes,
  StudentResults,
  ViewStudentResult,
  UploadStudentResult,
  StudentAttendance,
  ViewStudentAttendance,
  ResultsClasses,
  AttendanceClasses,
  SettlementDetails,
  AddQuestions,
  EditQuestions,
  TeacherTests,
  TeacherTestResults,
  Settings,
  TeacherSettings,
  AdminManagement,
  ViewAdmin,
  AllEnrollments,
  CreateEnrollmentForm,
  UpdateEnrollmentForm,
  ParentEnrollmentForm,
  EnrollmentDetails,
  SubmissionStatus,
  Announcements,
  UploadClassResult,
  AddResultSetting,
  TeacherUploadClassResult,
  DownloadApp,
  AddSchool,
  CBTExams,
  ViewTestQuestions,
  TestResults,
  AddResultMetrics,
  Billing,
  BillingSuccess,
  Funds,
  FundDetail,
  FinanceExpenses,
  ExpenseCategories,
  FinanceDashboard,
  FinanceReports,
  FundTransfer,
  Reconciliation,
  PayrollSetup,
  SalaryBatches,
  SalaryBatchDetail,
  ClassGroupDetail,
} from "./pages";
import SubscriptionGuard from "./components/subscription/SubscriptionGuard";
import SubscriptionReminderModal from "./components/subscription/SubscriptionReminderModal";
import "./App.css";
import { useStateContext } from "./contexts/ContextProvider";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const App = () => {
  const { user, school, setCurrentColor, setCurrentMode, currentMode, activeMenu, currentColor } =
    useStateContext();

  useEffect(() => {
    const currentThemeColor = localStorage.getItem("colorMode");
    const currentThemeMode = localStorage.getItem("themeMode");
    if (currentThemeColor && currentThemeMode) {
      setCurrentColor(currentThemeColor);
      setCurrentMode(currentThemeMode);
    }
  }, [setCurrentColor, setCurrentMode]);

  //console.log(user + " from context");
  return (
    <ErrorBoundary>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        limit={3}
        toastClassName="mobile-toast"
        bodyClassName="mobile-toast-body"
        progressClassName="mobile-toast-progress"
      />
      <div className={currentMode === "Dark" ? "dark" : ""}>
        <BrowserRouter>
          <Switch>
            {/* Public Routes */}
            <Route path="/:schoolSlug/submissions/:userEmail" component={SubmissionStatus} />
            <Route
              path="/:schoolSlug/admission/:levelOfEducation"
              component={ParentEnrollmentForm}
            />
            <Route path="/signin" component={SignIn} />
            <Route path="/signup" component={SignUp} />
            <Route path="/welcome/school" component={Welcome} />
            <Route path="/auth/reset" component={Reset} />
            <Route path="/auth/recover" component={Recover} />
            <Route path="/invite/accept/:id" component={AcceptInvite} />
            <Route path="/download-app" component={DownloadApp} />
            <Route path="/add-school" component={AddSchool} />

            {/* Private Routes */}
            <PrivateRoute path="/onboarding/kyc" component={Onboarding} />
            <PrivateRoute path="/onboarding/status" component={Completed} />

            {/* Dashboard Layout */}
            <div className="flex relative dark:bg-main-dark-bg">
              <div className="fixed right-4 bottom-4" style={{ zIndex: "1000" }}>
                <TooltipComponent content="Support" position="Top">
                  <Link to="/support">
                    <button
                      type="button"
                      style={{ background: currentColor, borderRadius: "50%" }}
                      className="text-3xl text-white p-3 hover:drop-shadow-xl hover:bg-light-gray"
                    >
                      <FaHeadset />
                    </button>
                  </Link>
                </TooltipComponent>
              </div>
              {activeMenu ? (
                <div className="w-72 fixed sidebar dark:bg-secondary-dark-bg bg-white shadow">
                  <Sidebar />
                </div>
              ) : (
                <div className="w-0 dark:bg-secondary-dark-bg">
                  <Sidebar />
                </div>
              )}
              <div
                className={
                  activeMenu
                    ? "dark:bg-main-dark-bg bg-gray-100 min-h-screen md:ml-72 w-full"
                    : "bg-gray-100 dark:bg-main-dark-bg w-full min-h-screen flex-2"
                }
              >
                <div className="fixed md:static bg-main-bg dark:bg-main-dark-bg navbar w-full">
                  <Navbar user={user} school={school} />
                </div>
                <SubscriptionReminderModal />
                <SubscriptionGuard>
                  <div>
                    {/* Billing */}
                    <PrivateRoute path="/billing/success" exact component={BillingSuccess} />
                    <PrivateRoute path="/billing" exact component={Billing} />
                    {/*  Admin Dashboard, Teachers Management */}
                    <PrivateRoute path="/management/teacher" exact component={TeacherManagement} />
                    <PrivateRoute path="/management/teacher/:id" exact component={ViewTeacher} />
                    <PrivateRoute
                      path="/management/teacher-attendance"
                      exact
                      component={TeacherAttendance}
                    />
                    <Route
                      path="/management/clock-attempts"
                      exact
                      render={() => <Redirect to="/management/teacher-attendance" />}
                    />
                    <PrivateRoute
                      path="/device-approval/:schoolId/requests"
                      exact
                      component={DeviceApprovalManagement}
                    />
                    <PrivateRoute
                      path="/management/attendance"
                      exact
                      component={AttendanceManagement}
                    />
                    <PrivateRoute
                      path="/management/clock-in-out"
                      exact
                      component={ClockInOutHistory}
                    />
                    <PrivateRoute
                      path="/students/view/:id/:classId"
                      exact
                      component={StudentProfile}
                    />
                    <PrivateRoute path="/management/admin" exact component={AdminManagement} />
                    <PrivateRoute path="/management/admin/:id" exact component={ViewAdmin} />
                    {/* dashboard  */}
                    <PrivateRoute path="/" exact component={Home} />
                    <PrivateRoute path="/home" component={Home} />
                    <PrivateRoute path="/onboarding/get-started" exact component={GetStarted} />
                    <PrivateRoute
                      path="/onboarding/get-started/documents"
                      exact
                      component={Documents}
                    />
                    <PrivateRoute
                      path="/onboarding/get-started/directors-identity"
                      exact
                      component={DirectorsIdentity}
                    />
                    <PrivateRoute path="/result-management" exact component={ResultManagement} />
                    <PrivateRoute path="/result-management/settings" component={AddResultSetting} />
                    <PrivateRoute path="/result-management/summary" component={Summary} />
                    <PrivateRoute
                      path="/result-management/upload-class-result"
                      component={UploadClassResult}
                    />
                    <PrivateRoute
                      path="/result-management/add-metrics"
                      component={AddResultMetrics}
                    />
                    <PrivateRoute path="/cbt-exams" exact component={CBTExams} />
                    <PrivateRoute
                      path="/cbt-exams/view/:testId"
                      exact
                      component={ViewTestQuestions}
                    />
                    <PrivateRoute
                      path="/cbt-exams/results/:testId/:schoolId"
                      exact
                      component={TestResults}
                    />

                    {/* pages  */}
                    <PrivateRoute path="/profile" component={Profile} />
                    <PrivateRoute path="/enrollments" exact component={AllEnrollments} />
                    <PrivateRoute
                      path="/enrollments/create"
                      exact
                      component={CreateEnrollmentForm}
                    />
                    <PrivateRoute
                      path="/enrollments/update/:templateId/:levelOfEducation"
                      exact
                      component={UpdateEnrollmentForm}
                    />
                    <PrivateRoute
                      path="/enrollments/submissions/:levelOfEducation"
                      exact
                      component={AllEnrollments}
                    />
                    <PrivateRoute
                      path="/enrollments/details/:id"
                      exact
                      component={EnrollmentDetails}
                    />
                    <PrivateRoute path="/classes" component={Classes} />
                    <PrivateRoute path="/class-groups/:classGroupId" component={ClassGroupDetail} />
                    <PrivateRoute path="/class-group/:classGroupId" component={ClassGroupDetail} />
                    <PrivateRoute path="/add-class" component={AddClass} />
                    <PrivateRoute path="/subjects/:classId" component={Subjects} />
                    <PrivateRoute path="/notes/:subjectId/:classId" component={LessonNotes} />
                    <PrivateRoute path="/update-class/:id" component={UpdateClass} />
                    <PrivateRoute path="/students/:classId" exact component={Students} />
                    <PrivateRoute path="/archived-students" exact component={ArchivedStudents} />
                    <PrivateRoute path="/graduated-students" exact component={GraduatedStudents} />
                    <PrivateRoute path="/update-student/:studentId" component={UpdateStudent} />
                    <PrivateRoute path="/addons/:id" component={Addons} />
                    <PrivateRoute path="/add-addons" component={AddAddons} />
                    <PrivateRoute path="/update-addon/:id" component={UpdateAddon} />
                    <PrivateRoute path="/transportation" component={Transportation} />
                    <PrivateRoute path="/add-transportation" component={AddTransportation} />
                    <PrivateRoute path="/update-transport/:id" component={UpdateTransport} />
                    <PrivateRoute path="/class-payments" component={ClassPayments} />
                    <PrivateRoute path="/all-payments" component={AllTransactions} />
                    <PrivateRoute path="/transaction-details/:id" component={TransactionDetails} />
                    <PrivateRoute path="/settlement-details/:id" component={SettlementDetails} />
                    <PrivateRoute
                      path="/class-transaction/:id/:class"
                      exact
                      component={TransactionPerClass}
                    />
                    <PrivateRoute
                      path="/class-transaction/payment/:id"
                      component={PaymentDetails}
                    />
                    <PrivateRoute path="/flexpay" exact component={Flexpay} />
                    <PrivateRoute path="/flexpay/flexpay-details/:id" component={FlexpayDetails} />
                    <PrivateRoute path="/flexpay-history" component={FlexpayHistory} />
                    <PrivateRoute path="/class-addons" exact component={ClassAddons} />
                    <PrivateRoute
                      path="/addon-payments/:id/:classId"
                      exact
                      component={AddonPayments}
                    />
                    <PrivateRoute path="/addon-payments/students/:id" component={PaymentPerAddon} />
                    <PrivateRoute path="/financing/cashflow" component={CashflowPending} />
                    <PrivateRoute path="/financing/details" component={InvoiceDetails} />
                    {/*<PrivateRoute path="/customers" component={Customers} />
                  <PrivateRoute path="/parents" component={Parents} />
                  <PrivateRoute path="/students" component={Students} />*/}
                    <PrivateRoute path="/withdraw" component={WithdrawBalance} />
                    <PrivateRoute path="/transfer" component={Transfer} />
                    <PrivateRoute path="/expenses" component={ExpenseRecord} />
                    <PrivateRoute path="/expense-details/:id" component={ExpenseDetails} />
                    <PrivateRoute path="/financing/withdraw" component={WithdrawCredit} />
                    {/* Finance (multi-fund) */}
                    <PrivateRoute path="/finance/dashboard" exact component={FinanceDashboard} />
                    <PrivateRoute path="/finance/funds" exact component={Funds} />
                    <PrivateRoute path="/finance/funds/:fundId" exact component={FundDetail} />
                    <PrivateRoute path="/finance/expenses" exact component={FinanceExpenses} />
                    <PrivateRoute
                      path="/finance/expense-categories"
                      exact
                      component={ExpenseCategories}
                    />
                    <PrivateRoute path="/finance/reports" exact component={FinanceReports} />
                    <PrivateRoute path="/finance/transfers" exact component={FundTransfer} />
                    <PrivateRoute path="/finance/reconciliation" exact component={Reconciliation} />
                    <PrivateRoute path="/finance/payroll/setup" exact component={PayrollSetup} />
                    <PrivateRoute path="/finance/payroll/batches" exact component={SalaryBatches} />
                    <PrivateRoute
                      path="/finance/payroll/batches/:batchId"
                      exact
                      component={SalaryBatchDetail}
                    />
                    <PrivateRoute path="/compliance/personal" component={Compliance} />
                    <PrivateRoute path="/compliance/business" component={BusinessCompliance} />
                    <PrivateRoute path="/compliance/payment" component={PaymentCompliance} />
                    <PrivateRoute path="/support" component={Support} />
                    <PrivateRoute path="/settings" component={Settings} />
                    <PrivateRoute path="/discounts" component={Discounts} />
                    <PrivateRoute path="/announcements" component={Announcements} />

                    {/* Teacher Module */}
                    <PrivateRoute path="/teacher" exact component={Dashboard} />
                    <PrivateRoute path="/t/classes" exact component={TeacherClassManagement} />
                    <PrivateRoute path="/t/students/:classId" exact component={TeacherStudents} />
                    <PrivateRoute path="/t/subjects" exact component={TeacherSubjects} />
                    <PrivateRoute
                      path="/t/subjects/notes/:subjectId/:classId"
                      component={TeacherLessonNotes}
                    />
                    <PrivateRoute path="/t/results" exact component={ResultsClasses} />
                    <PrivateRoute
                      path="/t/upload-class-result"
                      component={TeacherUploadClassResult}
                    />
                    <PrivateRoute
                      path="/t/results/students/:classId"
                      exact
                      component={StudentResults}
                    />
                    <PrivateRoute
                      path="/t/results/student/:resultsId"
                      component={ViewStudentResult}
                    />
                    <PrivateRoute
                      path="/t/results/upload/:resultsId"
                      component={UploadStudentResult}
                    />
                    <PrivateRoute path="/t/attendance" exact component={AttendanceClasses} />
                    <PrivateRoute
                      path="/t/attendance/mark/:classId"
                      exact
                      component={StudentAttendance}
                    />
                    <PrivateRoute
                      path="/t/attendance/view/:studentId"
                      component={ViewStudentAttendance}
                    />
                    <PrivateRoute path="/t/tests" exact component={TeacherTests} />
                    <PrivateRoute path="/t/tests/questions" component={AddQuestions} />
                    <PrivateRoute path="/t/tests/edit-questions" component={EditQuestions} />
                    <PrivateRoute
                      path="/t/tests/results/:testId/:schoolId"
                      component={TeacherTestResults}
                    />
                    <PrivateRoute path="/t/settings" exact component={TeacherSettings} />
                  </div>
                </SubscriptionGuard>
                <Footer />
              </div>
            </div>
            <Route component={NotFound} />
          </Switch>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
};

export default App;
